import React from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { NumericFormat } from "react-number-format";

export interface CrudFormField {
  name: string;
  label: string;
  type: "text" | "number" | "email" | "tel" | "select" | "textarea" | "currency" | "images" | "tree-select";
  required?: boolean;
  placeholder?: string;
  options?: { value: string; label: string }[]; // para select
  min?: number;
  max?: number;
  step?: number;
}

interface CrudFormProps {
  fields: CrudFormField[];
  form: Record<string, any>;
  setForm: (f: any) => void;
  onSubmit: (e: React.FormEvent) => void;
  onCancel?: () => void;
  submitLabel?: string;
  cancelLabel?: string;
  loading?: boolean;
  onImagesChange?: (files: FileList) => void;
  customRenderers?: { [fieldName: string]: (props: { value: any, onChange: (v: any) => void, field: CrudFormField }) => React.ReactNode };
}

export const CrudForm: React.FC<CrudFormProps> = ({
  fields,
  form,
  setForm,
  onSubmit,
  onCancel,
  submitLabel = "Salvar",
  cancelLabel = "Cancelar",
  loading = false,
  onImagesChange,
  customRenderers,
}) => {
  const handleInput = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const renderField = (field: CrudFormField) => {
    if (field.type === "tree-select" && customRenderers && customRenderers[field.name]) {
      return (
        <div key={field.name} className="flex flex-col gap-1">
          <label className="font-medium text-sm">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          {customRenderers[field.name]({ value: form[field.name], onChange: v => setForm({ ...form, [field.name]: v }), field })}
        </div>
      );
    }
    if (field.type === "currency") {
      return (
        <div key={field.name} className="flex flex-col gap-1">
          <label htmlFor={field.name} className="font-medium text-sm">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <NumericFormat
            id={field.name}
            name={field.name}
            value={form[field.name] || ""}
            onValueChange={({ floatValue }) => setForm({ ...form, [field.name]: floatValue ?? "" })}
            thousandSeparator="."
            decimalSeparator="," 
            prefix="R$ "
            allowNegative={false}
            decimalScale={2}
            fixedDecimalScale
            placeholder={field.placeholder || field.label}
            required={field.required}
            className="w-full rounded-md border px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          />
        </div>
      );
    }
    if (field.type === "images") {
      return (
        <div key={field.name} className="flex flex-col gap-1">
          <label htmlFor={field.name} className="font-medium text-sm">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <Input
            id={field.name}
            name={field.name}
            type="file"
            multiple
            accept="image/*"
            onChange={e => onImagesChange && e.target.files && onImagesChange(e.target.files)}
          />
        </div>
      );
    }
    return (
      <div key={field.name} className="flex flex-col gap-1">
        <label htmlFor={field.name} className="font-medium text-sm">
          {field.label}
          {field.required && <span className="text-red-500 ml-1">*</span>}
        </label>
        {field.type === "select" && field.options ? (
          <Select value={form[field.name] || ""} onValueChange={v => setForm({ ...form, [field.name]: v })}>
            <SelectTrigger id={field.name} name={field.name} className="w-full">
              <SelectValue placeholder={field.placeholder || field.label} />
            </SelectTrigger>
            <SelectContent>
              {field.options.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : field.type === "textarea" ? (
          <textarea
            id={field.name}
            name={field.name}
            value={form[field.name] || ""}
            onChange={handleInput}
            placeholder={field.placeholder || field.label}
            required={field.required}
            className="w-full rounded-md border px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            rows={3}
          />
        ) : (
          <Input
            id={field.name}
            name={field.name}
            type={field.type}
            value={form[field.name] || ""}
            onChange={handleInput}
            placeholder={field.placeholder || field.label}
            required={field.required}
            min={field.min}
            max={field.max}
            step={field.step}
          />
        )}
      </div>
    );
  };

  return (
    <div className="max-h-[80vh] overflow-y-auto px-4">
      <form onSubmit={onSubmit} className="space-y-6">
        <div className="text-xs text-muted-foreground mb-2">* Campos obrigatórios</div>
        {fields.map(renderField)}
        <div className="flex justify-end gap-2">
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>
              {cancelLabel}
            </Button>
          )}
          <Button type="submit" disabled={loading}>
            {submitLabel}
          </Button>
        </div>
      </form>
    </div>
  );
}; 